import { create } from 'zustand';

import { type UserPermission } from '@workinance/shared/data/permissions';
import { type IAuthenticatedUser } from '@workinance/shared/interfaces/auth.interface';
import { type IBusiness } from '@workinance/shared/interfaces/business.interface';
import {
	UserStatusEnum,
	UserTypeEnum,
} from '@workinance/shared/interfaces/user.interface';
import { useAlertStore } from '@workinance/shared/stores/alert.store';
import {
	applyPreferredTheme,
	useThemeStore,
	type ThemeName,
} from '@workinance/shared/stores/theme.store';

import {
	clearAuthData,
	getAuthData,
	saveAuthData,
	type IAuthData,
} from '@/lib/auth-storage';
import { sendHttpRequest } from '@/lib/http';

interface IAuthStore {
	user: IAuthenticatedUser | null;
	token: string | null;
	/** Renews `token` when it expires. Saved in the same record as the token. */
	refreshToken: string | null;
	/** The business everything in the dashboard works in. */
	business: IBusiness | null;
	/** Every business an owner has; a team member only has their own. */
	businesses: IBusiness[];
	/** What the signed-in user may do in this business, as the server works it out. Owners may do everything. */
	permissions: UserPermission[];
	/** False until the permissions have been fetched once this session, so pages can wait instead of turning people away. */
	hasLoadedPermissions: boolean;
	isAuthenticated: boolean;
	/** Whether the signed-in account owns its businesses, rather than being a team member of one. */
	isOwner: () => boolean;

	login: (authData: IAuthData) => void;
	/** Makes another of the owner's businesses the one the dashboard works in, then reloads into it. */
	switchBusiness: (businessId: string) => Promise<void>;
	logout: () => void;
	/**
	 * Swaps in a fresh access token after the refresh token renewed it, and saves it with the session. A
	 * staff session's refresh also carries the business as it is now; passing it here is how a change made
	 * elsewhere (e.g. the admin app) reaches an already-signed-in session. Omitted (an owner's refresh) keeps
	 * the business unchanged. Same for `freshPermissions`: a staff refresh carries them so a role change
	 * (or a role getting deleted) reaches the menu without a separate request; an owner's has none to carry,
	 * since owners always pass every permission check regardless.
	 */
	setAccessToken: (
		token: string,
		freshBusiness?: IBusiness | null,
		freshPermissions?: UserPermission[] | null,
	) => Promise<void>;
	/** Records that the signed-in user finished setting up their account. */
	markAccountActivated: () => void;
	/** Applies the notification choices right away and saves them to the user's account. */
	updateNotificationSettings: (changes: {
		acceptEmailNotification?: boolean;
		acceptSmsNotification?: boolean;
	}) => Promise<void>;
	/** Applies a theme right away and saves it to the user's account. */
	updatePreferredTheme: (theme: ThemeName) => Promise<void>;
	/** Who the user is comes from the device's stored session on every start. Nothing renders until this resolves. */
	restoreAuthentication: () => Promise<void>;
	/** Fetches an owner's businesses again, so changes made in the admin app show up. */
	refreshOwnerBusinesses: () => Promise<void>;
	/** Fetches what the signed-in user may do in the current business, so the menu shows only what they can open. Skips the request once loaded this session unless `force`. */
	loadPermissions: (force?: boolean) => Promise<void>;
}

let pendingPermissionsRequest: Promise<void> | null = null;

export const useAuthStore = create<IAuthStore>((set, get) => ({
	user: null,
	token: null,
	refreshToken: null,
	business: null,
	businesses: [],
	permissions: [],
	hasLoadedPermissions: false,
	isAuthenticated: false,
	isOwner: () => isOwner(get().user),

	login: (authData) => {
		const businesses =
			authData.businesses ?? (authData.business ? [authData.business] : []);
		const business = authData.business ?? businesses[0] ?? null;
		void saveAuthData<IAuthData>({ ...authData, business, businesses });
		set({
			user: authData.user,
			token: authData.token,
			refreshToken: authData.refreshToken ?? null,
			business,
			businesses,
			isAuthenticated: true,
		});
		applyPreferredTheme(authData.user.preferredTheme);
		void get().loadPermissions();
	},

	switchBusiness: async (businessId) => {
		const { user, token, businesses } = get();
		const business = businesses.find((item) => item.id === businessId);
		if (!user || !token || !business) return;

		// Saved before the reload so the session comes back in the new business. Reloading starts every
		// store fresh under it, rather than trusting each one to notice the switch and fetch again.
		await saveAuthData<IAuthData>({ token, user, business, businesses }).catch(
			() => {},
		);
		window.location.assign('/dashboard');
	},

	logout: () => {
		// Tell the server so the refresh token stops working everywhere, not just on this device. If the
		// request fails the device is still signed out locally.
		const { refreshToken } = get();
		if (refreshToken) {
			void sendHttpRequest({
				url: 'auth/logout',
				method: 'post',
				data: { refreshToken },
			}).catch(() => {});
		}
		void clearAuthData();
		set({
			user: null,
			token: null,
			refreshToken: null,
			business: null,
			businesses: [],
			permissions: [],
			hasLoadedPermissions: false,
			isAuthenticated: false,
		});
	},

	setAccessToken: async (token, freshBusiness, freshPermissions) => {
		const { user, businesses } = get();
		const business = freshBusiness ?? get().business;
		set({
			token,
			business,
			...(freshPermissions
				? { permissions: freshPermissions, hasLoadedPermissions: true }
				: {}),
		});
		if (user)
			await saveAuthData<IAuthData>({
				token,
				user,
				business,
				businesses,
			}).catch(() => {});
	},

	markAccountActivated: () => {
		const { user, token, business, businesses } = get();
		if (!user || !token) return;

		const activatedUser = { ...user, status: UserStatusEnum.ACTIVE };
		set({ user: activatedUser });
		void saveAuthData<IAuthData>({
			token,
			user: activatedUser,
			business,
			businesses,
		});
	},

	updatePreferredTheme: async (theme) => {
		const { user, token, business, businesses } = get();
		if (!user || !token) return;

		// The theme only switches once the server has saved it, so what is on screen always matches the account.
		try {
			await sendHttpRequest({
				url: 'users/me/settings',
				method: 'patch',
				data: { preferredTheme: theme },
			});

			useThemeStore.getState().setTheme(theme);
			const updatedUser = { ...user, preferredTheme: theme };
			set({ user: updatedUser });
			void saveAuthData<IAuthData>({
				token,
				user: updatedUser,
				business,
				businesses,
			});
		} catch (error) {
			useAlertStore
				.getState()
				.showError(error, 'Could not save your theme. Please try again.');
		}
	},

	updateNotificationSettings: async (changes) => {
		const { user, token, business, businesses } = get();
		if (!user || !token) return;

		// Show the new choice straight away; the save below makes it stick on the account.
		const previousUser = user;
		const updatedUser = {
			...user,
			settings: { ...user.settings, ...changes },
		};
		set({ user: updatedUser });
		void saveAuthData<IAuthData>({
			token,
			user: updatedUser,
			business,
			businesses,
		}).catch(() => {});

		try {
			await sendHttpRequest({
				url: 'users/me/settings',
				method: 'patch',
				data: changes,
			});
			useAlertStore.getState().showSuccess('Notification settings saved.');
		} catch (error) {
			set({ user: previousUser });
			void saveAuthData<IAuthData>({
				token,
				user: previousUser,
				business,
				businesses,
			}).catch(() => {});
			useAlertStore
				.getState()
				.showError(
					error,
					'Could not save your notification settings. Please try again.',
				);
		}
	},

	async restoreAuthentication() {
		const authData = await getAuthData<IAuthData>();
		// Every session works in a business (owners without one are not let in), so one without it is unusable.
		if (!authData?.token || !authData.business) {
			if (authData) void clearAuthData();
			set({
				user: null,
				token: null,
				refreshToken: null,
				business: null,
				businesses: [],
				permissions: [],
				hasLoadedPermissions: false,
				isAuthenticated: false,
			});
			return;
		}

		set({
			user: authData.user,
			token: authData.token,
			refreshToken: authData.refreshToken ?? null,
			business: authData.business,
			businesses: authData.businesses ?? [authData.business],
			isAuthenticated: true,
		});
		applyPreferredTheme(authData.user.preferredTheme);

		// An owner may have added, renamed or changed a business in the admin app since signing in.
		if (isOwner(authData.user)) void get().refreshOwnerBusinesses();
		void get().loadPermissions();
	},

	loadPermissions: (force = false) => {
		if (!force && get().hasLoadedPermissions) return Promise.resolve();
		// Restoring the session and a route guard can both ask at once; they share one request.
		pendingPermissionsRequest ??= (async () => {
			try {
				const { data: response } = await sendHttpRequest<{
					user: { permissions?: UserPermission[]; business?: IBusiness };
				}>({ url: 'users/me', method: 'get' });
				set({ permissions: response.user?.permissions ?? [] });
				// The server re-reads the business fresh on every request anyway (to resolve who's
				// asking), so this one already-happening call is also how a changed business reaches
				// a team member's session, without a dedicated refresh they have no endpoint for.
				const freshBusiness = response.user?.business;
				if (freshBusiness) {
					set({ business: freshBusiness });
					const { token, user, businesses } = get();
					if (token && user) {
						void saveAuthData<IAuthData>({
							token,
							user,
							business: freshBusiness,
							businesses,
						});
					}
				}
			} catch {
				// Without them the team pages stay out of the menu; the server still decides what each request may do.
			} finally {
				set({ hasLoadedPermissions: true });
				pendingPermissionsRequest = null;
			}
		})();
		return pendingPermissionsRequest;
	},

	refreshOwnerBusinesses: async () => {
		try {
			const { data: response } = await sendHttpRequest<{
				businesses: IBusiness[];
			}>({ url: 'businesses', method: 'get' });
			const businesses = response.businesses ?? [];
			const { user, token, business } = get();
			const activeBusiness = businesses.find(
				(item) => item.id === business?.id,
			);
			if (!user || !token) return;
			// The business this session works in is gone: sign out rather than show another business's data.
			if (!activeBusiness) {
				get().logout();
				return;
			}
			set({ business: activeBusiness, businesses });
			void saveAuthData<IAuthData>({
				token,
				user,
				business: activeBusiness,
				businesses,
			}).catch(() => {});
		} catch {
			// Keep the saved list rather than clearing it on a network hiccup.
		}
	},
}));

/** Whether the signed-in account owns its businesses, rather than being a team member of one. */
export function isOwner(user: IAuthenticatedUser | null) {
	return user?.type === UserTypeEnum.OWNER;
}

/**
 * Whether this session satisfies a list of required permissions: owners always do, no requirements
 * means everyone does, and anyone else needs at least one of them. The one check behind both `useCanView`
 * (hiding a piece of UI) and `canNavigate` (whether a route is reachable at all).
 */
export function hasPermission(
	state: Pick<IAuthStore, 'user' | 'permissions'>,
	requiredPermissions?: UserPermission[],
) {
	if (!requiredPermissions?.length) return true;
	return (
		isOwner(state.user) ||
		requiredPermissions.some((permission) =>
			state.permissions.includes(permission),
		)
	);
}

/** Whether to show something gated by these permissions, e.g. a button or a whole card. For a component. */
export function useCanView(requiredPermissions?: UserPermission[]) {
	return useAuthStore((state) => hasPermission(state, requiredPermissions));
}

/** Whether the current session may open a route needing these permissions. For a route loader, outside React. */
export function canNavigate(requiredPermissions?: UserPermission[]) {
	return hasPermission(useAuthStore.getState(), requiredPermissions);
}
